// What one cited source shows under an Ask Sophie answer.
//
// Since tikcare_rag returns only the sources the answer actually cites, each one
// carries `n` (the [n] marker in the answer text), `filename`, `page_no` or the
// sheet rows, the chunk `excerpt`, `uploaded_at` and `document_date`. Older answers saved before
// that change carry only `title` + `excerpt`; everything here falls back to those.

// What kind of document the source is, from the `type` tikcare_rag derives from
// the document key: "Report" / "Session transcript" / "Attachment". '' for
// answers saved before `type` existed, or an unknown value — the caption is
// simply left out.
const KIND_LABELS = { report: 'Report', transcript: 'Session transcript', attachment: 'Attachment' };
export function kindLabel(source) {
  return KIND_LABELS[source?.type] || '';
}

// "lab.pdf" from the source, else the part of the legacy title before the location.
export function sourceFileName(source) {
  if (source?.filename) return source.filename;
  const title = source?.title || '';
  return title.split(', page ')[0].split(', sheet ')[0].split(', rows ')[0] || 'Document';
}

// "page 2" / "sheet Labs, rows 4–9" / "rows 4–9" / "" (DOCX, HTML, images have no page).
export function sourceLocation(source) {
  if (!source) return '';
  if (source.page_no) return `page ${source.page_no}`;
  if (source.row_start) {
    const rows = source.row_end && source.row_end !== source.row_start
      ? `rows ${source.row_start}–${source.row_end}`
      : `row ${source.row_start}`;
    return source.sheet_name ? `sheet ${source.sheet_name}, ${rows}` : rows;
  }
  return '';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "Uploaded 12 Sep 2026". The upload date, not the document's own date — the label
// says so, because a lab collected in March can be uploaded in September.
export function uploadedLabel(source) {
  if (!source?.uploaded_at) return '';
  const d = new Date(source.uploaded_at);
  if (Number.isNaN(d.getTime())) return '';
  // Spelled out rather than toLocaleDateString: browsers disagree on "Sep" vs "Sept".
  return `Uploaded ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

// "Dated 10 Mar 2026" when the engine read the document's own date (an explicit label such
// as "Collected" or "評估日期" at ingest), else the upload date. document_date is a plain
// YYYY-MM-DD, split by hand so no time zone can move it by a day.
export function dateLabel(source) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(source?.document_date || '');
  if (m) {
    const month = MONTHS[Number(m[2]) - 1];
    if (month) return `Dated ${Number(m[3])} ${month} ${m[1]}`;
  }
  return uploadedLabel(source);
}

// The first ~max characters of the excerpt on one line, cut at a word boundary.
export function sourceSnippet(source, max = 180) {
  const text = String(source?.excerpt || '').replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

// Header text: "Sources (1)" / "Sources (3)".
export function sourcesHeader(count) {
  return `Sources (${count})`;
}
